import { Component } from '@theme/component';

/**
 * Accessible product toggle for the Product Showcase section.
 * Swaps between product panels via a WAI-ARIA tablist. When the section has no
 * toggle there are no tabs and every panel stays visible.
 *
 * Uses explicit listeners scoped to `this` rather than the ref/declarative-event
 * system, because each panel nests several custom elements (gallery, swatches,
 * product form) and we want the toggle to work regardless of that subtree.
 *
 * @extends {Component}
 */
export class NaProductShowcaseComponent extends Component {
    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();
        const { signal } = this.#abortController;

        this.#syncTabLabels();

        for (const tab of this.#tabs) {
            tab.addEventListener('click', () => this.#activate(tab, true), { signal });
            tab.addEventListener('keydown', this.#handleKeydown, { signal });
        }
    }

    /**
     * Mirror each panel's product title onto its tab. The product title resolves
     * reliably inside the block (the panel), so this guarantees the tab shows the
     * real product name even when the section-level setting doesn't resolve.
     */
    #syncTabLabels() {
        for (const tab of this.#tabs) {
            const panelId = tab.getAttribute('aria-controls');
            if (!panelId) continue;
            const panel = this.querySelector(`#${CSS.escape(panelId)}`);
            if (!panel) continue;

            const title = panel.querySelector('.na-product-showcase__title')?.textContent?.trim();
            const labelEl = tab.querySelector('.na-product-showcase__tab-label');
            if (title && labelEl) labelEl.textContent = title;

            // Toggle badge resolves on the block (panel), not in the section loop.
            const badge = panel.getAttribute('data-toggle-badge')?.trim();
            const badgeEl = tab.querySelector('.na-product-showcase__tab-badge');
            if (badgeEl) {
                if (badge) {
                    badgeEl.textContent = badge;
                    badgeEl.hidden = false;
                } else {
                    badgeEl.hidden = true;
                }
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    /** @returns {HTMLButtonElement[]} */
    get #tabs() {
        return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
    }

    /** @returns {HTMLElement[]} */
    get #panels() {
        return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-showcase-panel]')));
    }

    /** @param {KeyboardEvent} event */
    #handleKeydown = (event) => {
        const tabs = this.#tabs;
        const currentIndex = tabs.findIndex((tab) => tab === event.target);
        if (currentIndex === -1) return;

        let nextIndex = null;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                nextIndex = (currentIndex + 1) % tabs.length;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = tabs.length - 1;
                break;
            default:
                return;
        }

        event.preventDefault();
        const nextTab = tabs[nextIndex];
        if (nextTab) this.#activate(nextTab, true);
    };

    /**
     * @param {HTMLButtonElement} activeTab
     * @param {boolean} setFocus
     */
    #activate(activeTab, setFocus) {
        for (const tab of this.#tabs) {
            const isActive = tab === activeTab;
            tab.setAttribute('aria-selected', String(isActive));
            tab.tabIndex = isActive ? 0 : -1;
            tab.classList.toggle('is-active', isActive);
        }

        const controlledId = activeTab.getAttribute('aria-controls');
        for (const panel of this.#panels) {
            const isActive = panel.id === controlledId;
            panel.hidden = !isActive;
            panel.classList.toggle('is-active', isActive);
        }

        if (setFocus) activeTab.focus();
    }
}

if (!customElements.get('na-product-showcase-component')) {
    customElements.define('na-product-showcase-component', NaProductShowcaseComponent);
}
